import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { clientsApi } from '@services/api';
import type { ClientOption, CreateClientInput } from '@types';

const inputClass =
  'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500';

interface ClientComboboxProps {
  value: string;
  onChange: (clientId: string) => void;
  error?: string;
}

/**
 * Client picker with inline-create (F2.2). Selecting "+ New client" opens a
 * dialog that POSTs /clients, then selects the newly created record.
 */
export function ClientCombobox({
  value,
  onChange,
  error,
}: ClientComboboxProps) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<CreateClientInput>({
    display_name: '',
    short_ref: '',
    contact: '',
  });

  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateClientInput) => clientsApi.create(input),
    onSuccess: async created => {
      await queryClient.invalidateQueries({ queryKey: ['clients'] });
      onChange(created.id);
      setDialogOpen(false);
      setForm({ display_name: '', short_ref: '', contact: '' });
      toast.success('Client created');
    },
    onError: () => toast.error('Could not create client'),
  });

  return (
    <div>
      <div className="flex gap-2">
        <select
          data-testid="client-select"
          aria-label="Client"
          value={value}
          onChange={e => onChange(e.target.value)}
          className={inputClass}
        >
          <option value="">Select a client…</option>
          {clients?.map((c: ClientOption) => (
            <option key={c.id} value={c.id}>
              {c.display_name}
              {c.short_ref ? ` (${c.short_ref})` : ''}
            </option>
          ))}
        </select>
        <button
          type="button"
          data-testid="client-new-btn"
          onClick={() => setDialogOpen(true)}
          className="whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-100"
        >
          + New
        </button>
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      <Transition appear show={dialogOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setDialogOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Dialog.Panel
                data-testid="client-create-dialog"
                className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
              >
                <Dialog.Title className="text-lg font-medium text-gray-900">
                  New client
                </Dialog.Title>
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Display name
                    </label>
                    <input
                      data-testid="client-name-input"
                      className={inputClass}
                      value={form.display_name}
                      onChange={e =>
                        setForm(f => ({ ...f, display_name: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Short ref (used in filenames)
                    </label>
                    <input
                      data-testid="client-shortref-input"
                      className={inputClass}
                      value={form.short_ref ?? ''}
                      onChange={e =>
                        setForm(f => ({ ...f, short_ref: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Contact person
                    </label>
                    <input
                      data-testid="client-contact-input"
                      className={inputClass}
                      value={form.contact ?? ''}
                      onChange={e =>
                        setForm(f => ({ ...f, contact: e.target.value }))
                      }
                    />
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setDialogOpen(false)}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    data-testid="client-create-submit"
                    disabled={
                      !form.display_name.trim() || createMutation.isPending
                    }
                    onClick={() => createMutation.mutate(form)}
                    className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    {createMutation.isPending ? 'Creating…' : 'Create client'}
                  </button>
                </div>
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

export default ClientCombobox;
